// Ubicación en el repo: src/lib/pdfExport.js
// Convierte un string de HTML (la misma plantilla que ya usan los
// documentos imprimibles del proyecto, ver examReport.jsx) en un PDF real,
// codificado en base64, para mandarlo como adjunto por correo (DECISIONS.md
// #119, "Enviar audiometría por correo", pedido de Nicolás 16-sep-2026,
// tema 2 de 5). Nuevo par de dependencias: `html2canvas` + `jspdf`.
//
// Por qué no se reutiliza `#print-area` (print.js): ese contenedor vive con
// `display:none` fuera de @media print (ver index.css), y html2canvas no
// puede capturar nada con `display:none` — el elemento no tiene caja de
// layout. Acá se arma un contenedor aparte, fuera de la pantalla visible
// (`position:fixed; left:-10000px`) pero SÍ con layout real, para que
// html2canvas lo pueda "fotografiar" tal cual se vería impreso.
//
// BUG encontrado (reportado por Nicolás, 22-sep-2026): el PDF adjunto no
// quedaba idéntico al documento impreso/descargado — el ancho usado acá
// era el de la hoja A4 completa (210mm), pero `@page { margin: 10mm; }`
// (index.css) hace que el navegador imprima con un área real de 190mm de
// ancho (210mm - 10mm de cada lado), y `#print-area` (sin ancho propio)
// ocupa ese 100% disponible — la plantilla de examReport.jsx trae
// `max-width:210mm`, así que al imprimir de verdad queda acotada a los
// 190mm reales, no a 210mm. Renderizar el HTML a 210mm acá (sin margen)
// hacía que todo se dispusiera un poco distinto (tablas, columnas, saltos
// de línea) que en el impreso real. Corregido: se replica exactamente el
// mismo ancho de contenido (190mm) y los mismos márgenes de 10mm que usa
// `@page`.
//
// "Ajustar a una hoja" (mismo día, pedido de Nicolás: "siempre quede en
// una hoja" — tanto el impreso como el adjunto del correo): antes, si el
// contenido no entraba en una página se repartía en varias — a pedido
// explícito de Nicolás eso se reemplazó por achicar todo proporcionalmente
// (como "ajustar a la página" de cualquier impresora), igual que el
// equivalente para el impreso en `print.js` (`fitOnePage`). Acá es más
// simple: como ya se capturó todo el contenido en un único canvas, si su
// alto real (a 190mm de ancho) supera el alto disponible de una hoja
// (277mm), se lo coloca en el PDF a un tamaño menor (nunca se cambia de
// tamaño de página, sigue siendo A4 completo) — mismo anclaje arriba a la
// izquierda que usa la versión impresa (`transform-origin:top left`), para
// que ambas queden visualmente iguales.
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { waitForImages } from './print';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PAGE_MARGIN_MM = 10; // igual a `@page { margin: 10mm; }` en index.css
const CONTENT_WIDTH_MM = A4_WIDTH_MM - PAGE_MARGIN_MM * 2; // 190mm — ancho real disponible al imprimir
const CONTENT_HEIGHT_MM = A4_HEIGHT_MM - PAGE_MARGIN_MM * 2; // 277mm — alto real disponible por hoja
const MM_TO_PX = 96 / 25.4; // 96dpi, mismo criterio que el resto del proyecto
const CONTENT_WIDTH_PX = Math.round(CONTENT_WIDTH_MM * MM_TO_PX);
const FIT_SAFETY = 0.98; // mismo pequeño margen extra que usa print.js

export async function htmlToPdfBase64(html) {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '-10000px';
  container.style.width = `${CONTENT_WIDTH_PX}px`;
  container.style.background = '#ffffff';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    await waitForImages(container);
    const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const naturalHeightMm = (canvas.height * CONTENT_WIDTH_MM) / canvas.width;

    let placedWidthMm = CONTENT_WIDTH_MM;
    let placedHeightMm = naturalHeightMm;
    if (naturalHeightMm > CONTENT_HEIGHT_MM) {
      const scale = (CONTENT_HEIGHT_MM / naturalHeightMm) * FIT_SAFETY;
      placedWidthMm = CONTENT_WIDTH_MM * scale;
      placedHeightMm = naturalHeightMm * scale;
    }

    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    pdf.addImage(imgData, 'JPEG', PAGE_MARGIN_MM, PAGE_MARGIN_MM, placedWidthMm, placedHeightMm);

    return pdf.output('datauristring').split(',')[1];
  } finally {
    document.body.removeChild(container);
  }
}
